import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, KeyRound, Copy, Loader2, Trash2, Plus, Pencil, Power } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { entryLinksFrom } from '@/lib/app-entry-links';
import AppEntryButtons from '@/components/admin/AppEntryButtons';

interface AppRow {
  id: string;
  client_id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  base_url: string;
  redirect_uris: string[];
  scopes: string[];
  webhook_url: string | null;
  embed_origins: string[];
  is_active: boolean;
  created_at: string;
  manifest: Record<string, unknown> | null;
}

type ManifestNavMode = 'append' | 'replace';

interface InstallationRow {
  id: string;
  tenant_site_id: string;
  installed_by_user_id: string;
  installed_at: string;
  revoked_at: string | null;
  scopes: string[];
  active_tokens: number;
}

interface DeliveryRow {
  id: string;
  event: string;
  status: 'pending' | 'delivered' | 'failed';
  response_status: number | null;
  attempts: number;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  created_at: string;
  target_url: string;
}

interface MountRow {
  id: string;
  path_prefix: string;
  default_path: string;
  label: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  app_installation_id: string;
}

interface MountFormState {
  path_prefix: string;
  default_path: string;
  label: string;
  icon: string;
  sort_order: number;
}

interface PurchaseRow {
  id: string;
  sku: string;
  name: string;
  price_cents: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'expired';
  provider: string;
  refund_amount_cents: number | null;
  refund_reason: string | null;
  refunded_at: string | null;
  created_at: string;
  completed_at: string | null;
  user_id: string;
  user_email: string | null;
}

const INITIAL_MOUNT_FORM: MountFormState = {
  path_prefix: '',
  default_path: '/',
  label: '',
  icon: '',
  sort_order: 0,
};

const AppDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [app, setApp] = useState<AppRow | null>(null);
  const [installations, setInstallations] = useState<InstallationRow[]>([]);
  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);

  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  const [uninstallTarget, setUninstallTarget] = useState<InstallationRow | null>(null);
  const [uninstalling, setUninstalling] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);

  const [mounts, setMounts] = useState<MountRow[]>([]);
  const [mountModalOpen, setMountModalOpen] = useState(false);
  const [editingMount, setEditingMount] = useState<MountRow | null>(null);
  const [mountForm, setMountForm] = useState<MountFormState>(INITIAL_MOUNT_FORM);
  const [savingMount, setSavingMount] = useState(false);

  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [refundTarget, setRefundTarget] = useState<PurchaseRow | null>(null);
  const [refundAmountCents, setRefundAmountCents] = useState<string>('');
  const [refundReason, setRefundReason] = useState<string>('');
  const [refunding, setRefunding] = useState(false);

  // Manifest editor state. The contract v1.6 surface is a JSON object; we
  // expose `nav` as a free-form JSON textarea (operator audience — schema is
  // documented at docs/apps-platform/contract-v1.6.md) and `nav_mode` as a
  // typed select. The backend re-validates the same shape it accepts on
  // POST /admin/v1/manifest, so a malformed payload comes back as a 400.
  const [navJson, setNavJson] = useState<string>('[]');
  const [navMode, setNavMode] = useState<ManifestNavMode>('append');
  // Contract v1.20: the app's own admin route ('/admin'). Empty removes it.
  const [adminPath, setAdminPath] = useState<string>('');
  const [savingManifest, setSavingManifest] = useState(false);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [manifestMessage, setManifestMessage] = useState<string | null>(null);

  useEffect(() => {
    if (id) void load();
  }, [id]);

  async function load() {
    try {
      setLoading(true);
      setError(null);
      // Required calls — failures here surface the page-level error state.
      const [detail, d] = await Promise.all([
        api.get<{ app: AppRow; installations: InstallationRow[] }>(`/admin/apps/${id}`),
        api.get<{ deliveries: DeliveryRow[] }>(`/admin/apps/${id}/deliveries?limit=25`),
      ]);
      setApp(detail.app);
      setInstallations(detail.installations);
      setDeliveries(d.deliveries);

      const manifest = detail.app.manifest ?? {};
      const nav = (manifest as Record<string, unknown>).nav;
      setNavJson(JSON.stringify(nav ?? [], null, 2));
      const mode = (manifest as Record<string, unknown>).nav_mode;
      setNavMode(mode === 'replace' ? 'replace' : 'append');
      const declared = (manifest as Record<string, unknown>).admin_path;
      setAdminPath(typeof declared === 'string' ? declared : '');

      // Optional v1.1 mounts call — older Rumio installs (or stub test mocks)
      // may not return this; show the empty state instead of breaking the page.
      try {
        const m = await api.get<{ mounts: MountRow[] }>(`/admin/apps/${id}/mounts`);
        setMounts(m?.mounts ?? []);
      } catch {
        setMounts([]);
      }

      // Optional v1.4 purchases call — show empty state on platforms that
      // haven't migrated yet.
      try {
        const p = await api.get<{ purchases: PurchaseRow[] }>(`/admin/apps/${id}/purchases?limit=25`);
        setPurchases(p?.purchases ?? []);
      } catch {
        setPurchases([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load app');
    } finally {
      setLoading(false);
    }
  }

  function openCreateMount() {
    setEditingMount(null);
    setMountForm(INITIAL_MOUNT_FORM);
    setMountModalOpen(true);
  }

  function openEditMount(m: MountRow) {
    setEditingMount(m);
    setMountForm({
      path_prefix: m.path_prefix,
      default_path: m.default_path,
      label: m.label ?? '',
      icon: m.icon ?? '',
      sort_order: m.sort_order,
    });
    setMountModalOpen(true);
  }

  async function submitMount() {
    if (!mountForm.path_prefix.trim()) {
      useUIStore.getState().addToast('Path prefix is required', 'error');
      return;
    }
    const payload = {
      path_prefix: mountForm.path_prefix.trim(),
      default_path: mountForm.default_path.trim() || '/',
      label: mountForm.label.trim() || null,
      icon: mountForm.icon.trim() || null,
      sort_order: mountForm.sort_order,
    };
    try {
      setSavingMount(true);
      if (editingMount) {
        await api.patch(`/admin/apps/${id}/mounts/${editingMount.id}`, payload);
        useUIStore.getState().addToast('Mount updated', 'success');
      } else {
        await api.post(`/admin/apps/${id}/mounts`, payload);
        useUIStore.getState().addToast('Mount point created', 'success');
      }
      setMountModalOpen(false);
      await load();
    } catch (e) {
      useUIStore.getState().addToast(
        e instanceof Error ? e.message : `Failed to ${editingMount ? 'update' : 'create'} mount`,
        'error'
      );
    } finally {
      setSavingMount(false);
    }
  }

  async function deactivateMount(mountId: string) {
    try {
      await api.delete(`/admin/apps/${id}/mounts/${mountId}`);
      useUIStore.getState().addToast('Mount deactivated', 'success');
      await load();
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to deactivate', 'error');
    }
  }

  async function reactivateMount(mountId: string) {
    try {
      await api.patch(`/admin/apps/${id}/mounts/${mountId}`, { is_active: true });
      useUIStore.getState().addToast('Mount reactivated', 'success');
      await load();
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to reactivate', 'error');
    }
  }

  async function rotateSecret() {
    try {
      setRotating(true);
      const res = await api.post<{ client_secret: string }>(`/admin/apps/${id}/rotate-secret`, {});
      setRotatedSecret(res.client_secret);
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to rotate secret', 'error');
    } finally {
      setRotating(false);
    }
  }

  async function saveManifest() {
    setManifestError(null);
    setManifestMessage(null);
    let parsedNav: unknown;
    try {
      parsedNav = JSON.parse(navJson || '[]');
    } catch (e) {
      setManifestError(`Nav JSON is invalid: ${(e as Error).message}`);
      return;
    }
    const nextManifest: Record<string, unknown> = {
      ...(app?.manifest ?? {}),
      nav: parsedNav,
      nav_mode: navMode,
    };
    const trimmedAdmin = adminPath.trim();
    if (trimmedAdmin) nextManifest.admin_path = trimmedAdmin;
    else delete nextManifest.admin_path;
    try {
      setSavingManifest(true);
      await api.patch(`/admin/apps/${id}`, { manifest: nextManifest });
      setManifestMessage('Manifest saved.');
      await load();
      setTimeout(() => setManifestMessage(null), 4000);
    } catch (e) {
      setManifestError(e instanceof Error ? e.message : 'Failed to save manifest');
    } finally {
      setSavingManifest(false);
    }
  }

  async function confirmUninstall() {
    if (!uninstallTarget) return;
    try {
      setUninstalling(true);
      await api.delete(`/admin/apps/${id}/installations/${uninstallTarget.id}`);
      useUIStore.getState().addToast('Installation revoked', 'success');
      setUninstallTarget(null);
      await load();
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to uninstall', 'error');
    } finally {
      setUninstalling(false);
    }
  }

  function openRefund(p: PurchaseRow) {
    setRefundTarget(p);
    setRefundAmountCents(String(p.price_cents));
    setRefundReason('');
  }

  async function confirmRefund() {
    if (!refundTarget) return;
    const amount = Number(refundAmountCents);
    if (!Number.isInteger(amount) || amount <= 0 || amount > refundTarget.price_cents) {
      useUIStore.getState().addToast(`Amount must be 1 – ${refundTarget.price_cents} cents`, 'error');
      return;
    }
    try {
      setRefunding(true);
      await api.post(`/admin/apps/${id}/purchases/${refundTarget.id}/refund`, {
        amount_cents: amount,
        ...(refundReason ? { reason: refundReason } : {}),
      });
      useUIStore.getState().addToast('Purchase refunded', 'success');
      setRefundTarget(null);
      await load();
    } catch (e) {
      useUIStore.getState().addToast(e instanceof Error ? e.message : 'Failed to refund', 'error');
    } finally {
      setRefunding(false);
    }
  }

  function formatMoney(cents: number, currency: string): string {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
    }
  }

  if (loading) return <LoadingSpinner />;
  if (error || !app) return <div role="alert" className="text-red-600">{error ?? 'App not found'}</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/apps" className="inline-flex items-center text-sm text-blue-600 hover:underline">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to apps
        </Link>
      </div>

      <div>
        <div className="flex items-center gap-3">
          {app.icon_url && <img src={app.icon_url} alt="" className="w-10 h-10 rounded" />}
          <div>
            <h1 className="text-2xl font-semibold">{app.name}</h1>
            <div className="text-sm text-gray-600 font-mono">{app.client_id}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <AppEntryButtons links={entryLinksFrom(mounts, app.manifest)} appName={app.name} size="md" />
            {app.is_active ? <Badge variant="green">Active</Badge> : <Badge variant="gray">Disabled</Badge>}
          </div>
        </div>
        {app.description && <p className="text-sm text-gray-700 mt-2">{app.description}</p>}
      </div>

      <Card>
        <h2 className="text-lg font-semibold mb-3">Credentials</h2>
        <div className="space-y-2 text-sm">
          <div><strong>Base URL:</strong> <code className="bg-gray-100 px-2 py-0.5 rounded">{app.base_url}</code></div>
          <div><strong>Redirect URIs:</strong>
            <ul className="list-disc list-inside">
              {app.redirect_uris.map((u) => <li key={u}><code>{u}</code></li>)}
            </ul>
          </div>
          <div><strong>Webhook URL:</strong> <code className="bg-gray-100 px-2 py-0.5 rounded">{app.webhook_url ?? '(none)'}</code></div>
          <div><strong>Embed origins:</strong>
            <ul className="list-disc list-inside">
              {app.embed_origins.map((o) => <li key={o}><code>{o}</code></li>)}
            </ul>
          </div>
          <div><strong>Allowed scopes:</strong> {app.scopes.map((s) => <code key={s} className="mr-1 bg-gray-100 px-1">{s}</code>)}</div>
        </div>
        <div className="mt-4">
          <Button onClick={rotateSecret} disabled={rotating} aria-label="Rotate client secret">
            {rotating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" />Rotate secret</>}
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold mb-3">Manifest (unified nav)</h2>
        <p className="text-sm text-gray-600 mb-4">
          Surfaced in the host header when this app is mounted. Apps may also
          override <code>nav</code> at runtime via <code>postMessage(rumio:nav)</code>;
          this manifest is the first-paint fallback. Schema: <code>docs/apps-platform/contract-v1.6.md</code>.
        </p>

        {manifestError && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
            {manifestError}
          </div>
        )}
        {manifestMessage && (
          <div className="mb-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800" role="status">
            {manifestMessage}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="manifest_nav_mode" className="block text-sm font-medium text-gray-700 mb-1">
              Nav mode
            </label>
            <select
              id="manifest_nav_mode"
              value={navMode}
              onChange={(e) => setNavMode(e.target.value === 'replace' ? 'replace' : 'append')}
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="append">append (marketing nav + app nav)</option>
              <option value="replace">replace (hide marketing, prepend "Back to site")</option>
            </select>
          </div>

          <div>
            <label htmlFor="manifest_admin_path" className="block text-sm font-medium text-gray-700 mb-1">
              Admin path
            </label>
            <Input
              id="manifest_admin_path"
              value={adminPath}
              onChange={(e) => setAdminPath(e.target.value)}
              placeholder="/admin"
              className="max-w-xs font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">
              The app's own admin area, as an app path. Adds an <strong>Open app admin</strong> button here and on
              the apps list, opening it on the site with your session. Leave empty if the app has none.
            </p>
          </div>

          <div>
            <label htmlFor="manifest_nav" className="block text-sm font-medium text-gray-700 mb-1">
              Nav entries (JSON)
            </label>
            <textarea
              id="manifest_nav"
              value={navJson}
              onChange={(e) => setNavJson(e.target.value)}
              rows={14}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-gray-500">
              Array of <code>{`{ label, path, icon?, badge?, children? }`}</code>. Paths must start with <code>/</code>.
              Icons are kebab-case lucide names (e.g. <code>book-open</code>).
            </p>
          </div>
        </div>

        <div className="mt-4">
          <Button onClick={saveManifest} disabled={savingManifest}>
            {savingManifest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save manifest'}
          </Button>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Installations</h2>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
              className="w-4 h-4"
            />
            Show revoked installations
          </label>
        </div>
        {(() => {
          const visible = installations.filter((i) => showRevoked || !i.revoked_at);
          if (visible.length === 0) {
            return (
              <div className="text-sm text-gray-500 py-4">
                {showRevoked ? 'No installations.' : 'No active installations.'}
              </div>
            );
          }
          return (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 px-2">Tenant</th>
                  <th className="py-2 px-2">Scopes</th>
                  <th className="py-2 px-2">Active Tokens</th>
                  <th className="py-2 px-2">Installed</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((i) => (
                  <tr key={i.id} className="border-b last:border-0">
                    <td className="py-2 px-2 font-mono text-xs">{i.tenant_site_id}</td>
                    <td className="py-2 px-2 text-xs">{i.scopes.join(', ')}</td>
                    <td className="py-2 px-2">{i.active_tokens}</td>
                    <td className="py-2 px-2 text-xs">{new Date(i.installed_at).toLocaleString()}</td>
                    <td className="py-2 px-2">
                      {i.revoked_at ? <Badge variant="gray">Revoked</Badge> : <Badge variant="green">Active</Badge>}
                    </td>
                    <td className="py-2 px-2">
                      {!i.revoked_at && (
                        <Button variant="ghost" onClick={() => setUninstallTarget(i)} aria-label={`Uninstall ${i.tenant_site_id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </Card>

      <Card>
        <h2 className="text-lg font-semibold mb-1">Mount points</h2>
        <p className="text-sm text-gray-600 mb-3">
          Map a path prefix on this site to the app. Visiting <code>/&lt;prefix&gt;</code> renders the app's
          iframe inside Rumio's shell. Requires an active installation.
        </p>
        {installations.filter((i) => !i.revoked_at).length === 0 ? (
          <div className="text-sm text-gray-500 py-4">Install the app on this tenant before adding a mount.</div>
        ) : (
          <div className="mb-3">
            <Button onClick={openCreateMount}>
              <Plus className="w-4 h-4 mr-1" />Add mount
            </Button>
          </div>
        )}
        {mounts.length === 0 ? (
          <div className="text-sm text-gray-500 py-4">No mount points configured.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 px-2">Prefix</th>
                <th className="py-2 px-2">Default path</th>
                <th className="py-2 px-2">Label</th>
                <th className="py-2 px-2">Order</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mounts.map((m) => (
                <tr key={m.id} className="border-b last:border-0">
                  <td className="py-2 px-2 font-mono text-xs">/{m.path_prefix}</td>
                  <td className="py-2 px-2 font-mono text-xs">{m.default_path}</td>
                  <td className="py-2 px-2 text-xs">{m.label ?? '—'}</td>
                  <td className="py-2 px-2">{m.sort_order}</td>
                  <td className="py-2 px-2">
                    {m.is_active ? <Badge variant="green">Active</Badge> : <Badge variant="gray">Inactive</Badge>}
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      {m.is_active ? (
                        <>
                          <Button variant="ghost" onClick={() => openEditMount(m)} aria-label={`Edit mount ${m.path_prefix}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" onClick={() => deactivateMount(m.id)} aria-label={`Deactivate mount ${m.path_prefix}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </>
                      ) : (
                        <Button variant="ghost" onClick={() => reactivateMount(m.id)} aria-label={`Reactivate mount ${m.path_prefix}`}>
                          <Power className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h2 className="text-lg font-semibold mb-3">Recent Purchases</h2>
        {purchases.length === 0 ? (
          <div className="text-sm text-gray-500 py-4">No one-shot purchases yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 px-2">SKU</th>
                <th className="py-2 px-2">Item</th>
                <th className="py-2 px-2">User</th>
                <th className="py-2 px-2">Amount</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 px-2">Created</th>
                <th className="py-2 px-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-2 px-2 font-mono text-xs">{p.sku}</td>
                  <td className="py-2 px-2">{p.name}</td>
                  <td className="py-2 px-2 text-xs">{p.user_email ?? p.user_id}</td>
                  <td className="py-2 px-2">{formatMoney(p.price_cents, p.currency)}</td>
                  <td className="py-2 px-2">
                    {p.status === 'completed' && <Badge variant="green">Completed</Badge>}
                    {p.status === 'pending' && <Badge variant="yellow">Pending</Badge>}
                    {p.status === 'failed' && <Badge variant="red">Failed</Badge>}
                    {p.status === 'refunded' && <Badge variant="gray">Refunded</Badge>}
                    {p.status === 'expired' && <Badge variant="gray">Expired</Badge>}
                  </td>
                  <td className="py-2 px-2 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="py-2 px-2 text-right">
                    <Button
                      variant="ghost"
                      onClick={() => openRefund(p)}
                      disabled={p.status !== 'completed'}
                      aria-label={`Refund ${p.sku}`}
                    >
                      Refund
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h2 className="text-lg font-semibold mb-3">Recent Deliveries</h2>
        {deliveries.length === 0 ? (
          <div className="text-sm text-gray-500 py-4">No webhook deliveries yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 px-2">Event</th>
                <th className="py-2 px-2">Target</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 px-2">HTTP</th>
                <th className="py-2 px-2">Attempts</th>
                <th className="py-2 px-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id} className="border-b last:border-0">
                  <td className="py-2 px-2 font-mono text-xs">{d.event}</td>
                  <td className="py-2 px-2 text-xs truncate max-w-xs">{d.target_url}</td>
                  <td className="py-2 px-2">
                    {d.status === 'delivered' && <Badge variant="green">Delivered</Badge>}
                    {d.status === 'pending' && <Badge variant="yellow">Pending</Badge>}
                    {d.status === 'failed' && <Badge variant="red">Failed</Badge>}
                  </td>
                  <td className="py-2 px-2">{d.response_status ?? '—'}</td>
                  <td className="py-2 px-2">{d.attempts}</td>
                  <td className="py-2 px-2 text-xs">{new Date(d.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Rotated secret modal */}
      <Modal isOpen={!!rotatedSecret} onClose={() => setRotatedSecret(null)} title="New client secret">
        {rotatedSecret && (
          <div className="space-y-3">
            <p className="text-sm text-red-700">
              Copy this now — Rumio cannot show it again. The previous secret is invalidated and the app must use this
              new value for token exchange.
            </p>
            <div className="flex gap-2 items-center">
              <code className="flex-1 bg-gray-100 px-2 py-1 rounded text-sm break-all">{rotatedSecret}</code>
              <Button
                variant="ghost"
                onClick={() => navigator.clipboard.writeText(rotatedSecret).then(() => useUIStore.getState().addToast('Secret copied', 'success'))}
                aria-label="Copy rotated secret"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setRotatedSecret(null)}>I've saved it</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Mount create/edit modal */}
      <Modal
        isOpen={mountModalOpen}
        onClose={() => setMountModalOpen(false)}
        title={editingMount ? `Edit mount /${editingMount.path_prefix}` : 'Add mount point'}
      >
        <div className="space-y-3">
          <Input
            label="Path prefix *"
            placeholder="trade"
            value={mountForm.path_prefix}
            onChange={(e) => setMountForm({ ...mountForm, path_prefix: e.target.value })}
            helperText="Lowercase letters, digits, hyphens, or underscores. Visiting /<prefix> on this site renders the app."
            autoFocus
          />
          <Input
            label="Default path inside app"
            placeholder="/"
            value={mountForm.default_path}
            onChange={(e) => setMountForm({ ...mountForm, default_path: e.target.value })}
            helperText="App route loaded when the bare prefix is visited. Must start with /."
          />
          <Input
            label="Nav label"
            placeholder="Trade"
            value={mountForm.label}
            onChange={(e) => setMountForm({ ...mountForm, label: e.target.value })}
          />
          <Input
            label="Nav icon"
            placeholder="lucide:trending-up"
            value={mountForm.icon}
            onChange={(e) => setMountForm({ ...mountForm, icon: e.target.value })}
            helperText="Optional. Names a Lucide icon (or other slug your shell understands)."
          />
          <Input
            label="Sort order"
            type="number"
            value={mountForm.sort_order}
            onChange={(e) => setMountForm({ ...mountForm, sort_order: Number(e.target.value) || 0 })}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setMountModalOpen(false)}>Cancel</Button>
            <Button onClick={submitMount} disabled={savingMount}>
              {savingMount ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingMount ? 'Save' : 'Add mount')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Refund confirmation */}
      <Modal isOpen={!!refundTarget} onClose={() => setRefundTarget(null)} title="Refund this purchase?">
        {refundTarget && (
          <div className="space-y-3">
            <p className="text-sm">
              Refund <strong>{refundTarget.name}</strong> ({refundTarget.sku}) sold for{' '}
              {formatMoney(refundTarget.price_cents, refundTarget.currency)} to{' '}
              <code>{refundTarget.user_email ?? refundTarget.user_id}</code>?
            </p>
            <p className="text-xs text-gray-600">
              The app will receive an <code>app_purchase.refunded</code> webhook so it can claw back the consumable
              balance. Stripe refunds typically settle in 5–10 days.
            </p>
            <Input
              label="Refund amount (cents)"
              type="number"
              value={refundAmountCents}
              onChange={(e) => setRefundAmountCents(e.target.value)}
              helperText={`Max: ${refundTarget.price_cents}. Partial refunds supported.`}
            />
            <Input
              label="Reason (optional)"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Customer support note"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setRefundTarget(null)}>Cancel</Button>
              <Button onClick={confirmRefund} disabled={refunding}>
                {refunding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Refund</>}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Uninstall confirmation */}
      <Modal isOpen={!!uninstallTarget} onClose={() => setUninstallTarget(null)} title="Uninstall this app?">
        {uninstallTarget && (
          <div className="space-y-3">
            <p className="text-sm">
              Uninstalling will revoke all access tokens for this installation on tenant{' '}
              <code>{uninstallTarget.tenant_site_id}</code>. The app will lose API access immediately.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setUninstallTarget(null)}>Cancel</Button>
              <Button onClick={confirmUninstall} disabled={uninstalling}>
                {uninstalling ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Uninstall</>}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AppDetail;
